import { useMemo, useState } from "react";
import SelectorDeDia from "./SelectorDeDia";
import { useTranslation } from "react-i18next";

import ExportButtons from "@/pages/reports/components/ExportButtons";
import type { ReportTableColumn } from "@/pages/reports/components/ReportPointsTable";

import { indiceDelDia, tonoDelIndice } from "../../../features/conduccion/indice";
import type { CameraEvent } from "../types";
import type { CentinelaConduccion, CentinelaPareja } from "../../../features/centinela/types";
import CentinelaPensando from "./CentinelaPensando";
import ReglaDelDia from "./ReglaDelDia";

/**
 * Cómo se manejó esta unidad en un día.
 *
 * ── Por qué aquí vive TODO lo de revisar ────────────────────────────────────
 * La pestaña de dashcam quedó sólo para ver en vivo: abrir la cámara y mirar.
 * Todo lo que es revisar después —la evidencia grabada, los eventos que detectó
 * la cámara, cómo se condujo— vive acá. Antes estaba repartido y el usuario
 * tenía que saber de antemano en cuál de las dos mirar; peor aún, la mitad de
 * la evidencia sólo se veía si había un caso abierto.
 *
 * ── Por qué NO exige cámara ─────────────────────────────────────────────────
 * Una unidad con GPS a secas tiene movimientos, incidencias y llamadas: dónde
 * estuvo y qué le pasó. La de cámara suma cómo se condujo y las imágenes. Es la
 * misma pantalla con más o menos detalle, no dos pantallas. Condicionarla al
 * equipo dejaría sin historial a la mayor parte de la flota, que es justo la
 * que más lo necesita porque de ella se sabe menos.
 *
 * ── Por qué se agrupa lo repetido ───────────────────────────────────────────
 * Un hecho suelto no informa: todos nos distraemos y toda unidad pierde señal
 * un rato. Lo que informa es la repetición y a qué hora se concentra. Un día
 * real trajo 30 salidas de carril y 16 pérdidas de señal; en lista cronológica
 * eso son 46 renglones que nadie lee y que ahogan los 3 movimientos, que era lo
 * único que contaba de verdad.
 *
 * ── Por qué hay indicadores sin valor ───────────────────────────────────────
 * Kilómetros, tiempo al volante, ralentí, tráfico y descanso se piden porque es
 * lo que se va a medir. Hoy el parte NO los trae: se calculan del flujo de
 * posiciones (odómetro y velocidad), que este endpoint no lee. Se muestran
 * marcados como pendientes y NUNCA en cero. Un «0 km» se lee como «no se
 * movió», que es una afirmación falsa; un guion se lee como «todavía no lo sé»,
 * que es la verdad.
 *
 * ── Por qué todo se lee a la defensiva ──────────────────────────────────────
 * Esta pantalla se puso negra en producción el 2026-09-23: el proxy ya estaba
 * desplegado pero el cerebro no, así que el parte llegó sin `conductas` y un
 * `conductas.length` tumbó el árbol entero de React. Front y backend se
 * despliegan por separado y SIEMPRE habrá una ventana en que uno va adelante.
 */

export interface ConduccionPanelProps {
  parte: CentinelaConduccion | null;
  /** El segundo equipo del vehículo, cuando lo hay. Decide de quién es cada
   *  número: sin esto, el ralentí medido se leería como si lo hubiera medido
   *  la cámara, que no manda el bit de encendido. */
  pareja?: CentinelaPareja | null;
  /** El día que efectivamente contestó el servidor (YYYY-MM-DD). */
  fecha: string;
  /** Lo que el usuario eligió en el selector; "" significa «hoy». */
  seleccion?: string;
  onFecha?: (fecha: string) => void;
  isLoading?: boolean;
  /** Recargando otro día con el anterior aún en pantalla. */
  isFetching?: boolean;
  /** Sólo decide qué explicar cuando no hay datos de cámara, nunca oculta la
   *  pestaña: sin cámara el vacío es esperable, con cámara es una noticia. */
  tieneCamara?: boolean;
  /** Lo que detectó la cámara, venga de caso o de bitácora. */
  cameraEvents?: CameraEvent[];
}

/** Una fila de la tabla que se descarga. Plana a propósito: Excel y PDF no
 *  saben de secciones anidadas, y así el archivo se lee como una bitácora. */
interface FilaParte {
  id: string;
  hora: string;
  seccion: string;
  evento: string;
  detalle: string;
  /** Geocerca si se conoce; si no, las coordenadas. Vacío cuando no se supo
   *  dónde: una celda vacía es más honesta que una dirección inventada. */
  ubicacion: string;
}

const COLUMNAS: ReportTableColumn<FilaParte>[] = [
  { key: "hora", header: "Hora", align: "left" },
  { key: "seccion", header: "Sección", align: "left" },
  { key: "evento", header: "Evento", align: "left" },
  { key: "detalle", header: "Detalle", align: "left" },
  { key: "ubicacion", header: "Ubicación", align: "left" },
];

const ESTADO_TRAMO: Record<string, string> = {
  conduciendo: "Conduciendo",
  trafico: "A vuelta de rueda",
  ralenti: "En ralentí",
  detenido: "Detenida",
  sin_datos: "Sin reportar",
};

/** Minutos como «8 h 12 m». `null` y `undefined` se quedan en null para que la
 *  tarjeta dibuje un guion en vez de un cero que afirmaría algo falso. */
function duracion(min: number | null | undefined): string | null {
  if (min == null || !Number.isFinite(min)) return null;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h} h ${m} m` : `${m} m`;
}

/**
 * Agrupa hechos repetidos conservando de cuándo a cuándo ocurrieron.
 *
 * Las horas llegan ya formateadas ("14:38") y como son del mismo día se
 * ordenan bien comparándolas como texto.
 */
function agrupar(lista: { hora: string; etiqueta: string }[]) {
  const porClave = new Map<string, { etiqueta: string; veces: number; primera: string; ultima: string }>();
  for (const x of lista) {
    if (!x.etiqueta) continue;
    const previo = porClave.get(x.etiqueta);
    if (!previo) {
      porClave.set(x.etiqueta, { etiqueta: x.etiqueta, veces: 1, primera: x.hora, ultima: x.hora });
      continue;
    }
    previo.veces += 1;
    if (x.hora < previo.primera) previo.primera = x.hora;
    if (x.hora > previo.ultima) previo.ultima = x.hora;
  }
  return [...porClave.values()].sort((a, b) => b.veces - a.veces);
}

/** Hoy en la zona del navegador, en YYYY-MM-DD. `en-CA` es el atajo estándar
 *  para ese formato sin pasar por UTC, que adelantaría el día. */
function hoyLocal() {
  return new Date().toLocaleDateString("en-CA");
}

const TONO_TRAZO: Record<string, string> = {
  success: "stroke-success",
  warning: "stroke-warning",
  error: "stroke-error",
  neutral: "stroke-outline",
  info: "stroke-info",
};

const TONO_TEXTO: Record<string, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
  neutral: "text-content-faint",
};

/**
 * El índice del día como anillo.
 *
 * Sin veredicto escrito a un lado. El diseño de referencia pone «EXCELLENT»
 * bajo el número, y eso sobre un índice que todavía no se normaliza por
 * kilómetros sería afirmar de una persona algo que el dato no sostiene. El
 * color ya comunica la banda; la palabra la ponemos cuando el número la
 * aguante.
 */
/**
 * El reloj de un indicador.
 *
 * Tres estados, y la diferencia entre ellos es deliberada:
 *
 *   `fraccion` entre 0 y 1 → aro parcial. El indicador es una PARTE de algo:
 *     tres horas y media de las diez que se midieron, veinte incidencias de
 *     cincuenta hechos. El arco dice cuánta parte.
 *
 *   `fraccion: null` con valor → aro COMPLETO. El indicador es un absoluto sin
 *     denominador: 248 km no son «de cuántos», cuatro zonas recurrentes
 *     tampoco. Se llena sobre sí mismo. No afirma un 100 % de nada — lo que
 *     comunica es «este número está completo», que es justamente lo contrario
 *     de un aro a medias esperando llenarse.
 *
 *   `fraccion: null` sin valor → aro punteado. No se sabe, que no es cero.
 */
function Reloj({
  fraccion,
  tono = "info",
  pendiente = false,
}: {
  fraccion: number | null;
  tono?: string;
  pendiente?: boolean;
}) {
  const r = 13;
  const vuelta = 2 * Math.PI * r;
  const avance = pendiente ? 0 : fraccion === null ? vuelta : Math.max(0, Math.min(1, fraccion)) * vuelta;

  return (
    <svg viewBox="0 0 32 32" className="h-8 w-8 shrink-0 -rotate-90" aria-hidden="true">
      <circle cx="16" cy="16" r={r} fill="none" strokeWidth="2.5" className="stroke-subtle" />
      <circle
        cx="16"
        cy="16"
        r={r}
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={pendiente ? "2 4" : `${avance} ${vuelta}`}
        className={
          pendiente ? "stroke-outline/60" : TONO_TRAZO[tono] || "stroke-info"
        }
      />
    </svg>
  );
}

function Anillo({ valor, formula }: { valor: number | null; formula: string }) {
  const tono = tonoDelIndice(valor);
  const r = 26;
  const vuelta = 2 * Math.PI * r;
  const avance = valor === null ? 0 : (valor / 100) * vuelta;

  return (
    <div className="flex shrink-0 flex-col items-center gap-1" title={formula}>
      <div className="relative h-[68px] w-[68px]">
        <svg viewBox="0 0 68 68" className="h-full w-full -rotate-90">
          <circle cx="34" cy="34" r={r} fill="none" strokeWidth="6" className="stroke-subtle" />
          <circle
            cx="34"
            cy="34"
            r={r}
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${avance} ${vuelta}`}
            className={TONO_TRAZO[tono]}
          />
        </svg>
        <span
          className={`absolute inset-0 flex items-center justify-center text-lg font-semibold tabular-nums ${TONO_TEXTO[tono]}`}
        >
          {valor === null ? "—" : valor}
        </span>
      </div>
      <span className="font-mono text-[8px] uppercase tracking-wider text-content-muted max-lg:text-[11px]">
        índice · provisional
      </span>
    </div>
  );
}

export default function ConduccionPanel({
  parte,
  fecha,
  pareja = null,
  seleccion = "",
  onFecha,
  isLoading,
  isFetching,
  tieneCamara = false,
  cameraEvents = [],
}: ConduccionPanelProps) {
  const { t } = useTranslation("common");

  // Nada de esto se destructura a secas: ver la nota sobre el negro.
  const resumen = parte?.resumen;
  const conductas = parte?.conductas ?? [];
  // Los hechos llegan en UNA lista con su tipo, no en cuatro listas: el mapa
  // pinta pines en orden y separarlos allá obligaba a volver a mezclarlos.
  const hechos = parte?.hechos ?? [];
  const movimientos = useMemo(() => hechos.filter((h) => h.tipo === "movimiento"), [hechos]);
  const incidencias = useMemo(() => hechos.filter((h) => h.tipo === "caso"), [hechos]);
  const conversaciones = parte?.llamadas ?? [];
  const costumbres = parte?.costumbres ?? null;
  const jornada = parte?.medicion ?? null;
  const motor = parte?.telemetria ?? null;
  const narrativa = parte?.narrativa ?? "";
  const unidad = parte?.unidad;

  const hoy = hoyLocal();
  const dia = seleccion || fecha || hoy;
  const [verRegla, setVerRegla] = useState(false);
  // Qué indicador está abierto. «20 incidencias» sin poder ver CUÁLES es un
  // número que no se puede usar: obliga a creerle o a irse a otra pantalla.
  const [abierto, setAbierto] = useState<string | null>(null);

  /** Medianoche del día mostrado, en la zona del navegador. La regla cubre 24 h
   *  desde ahí; sin un ancla fija los tramos se dibujarían corridos. */
  const inicioDia = useMemo(() => {
    const [y, m, d] = dia.split("-").map(Number);
    return y && m && d ? new Date(y, m - 1, d, 0, 0, 0, 0).getTime() : 0;
  }, [dia]);


  /**
   * Los ocho indicadores.
   *
   * `valor: null` NO es cero: es «no lo sé todavía», y se dibuja distinto. Los
   * cinco pendientes salen del flujo de posiciones (odómetro, velocidad), que
   * el parte no lee; cuando lo lea, basta llenar el valor aquí.
   */
  /**
   * Los ocho indicadores, con su reloj.
   *
   * El denominador de los tiempos es el TIEMPO MEDIDO del día —conduciendo más
   * detenido más los huecos—, no 24 horas: de una unidad que reportó seis horas
   * no se sabe nada de las otras dieciocho, y un aro contra 24 h la pintaría
   * como si hubiera estado parada. El de los conteos es el total de hechos.
   *
   * Los kilómetros van SIN reloj a propósito. No hay «de cuántos»: la única
   * forma de dibujar un aro sería contra una meta diaria que nadie fijó, y eso
   * es decoración con aspecto de dato.
   */
  const indicadores = useMemo(() => {
    const DIA_MIN = 24 * 60;

    /*
      ── El denominador de cada reloj ────────────────────────────────────────
      No es el mismo para todos, y forzar uno solo daba aros mentirosos:

        tiempos del día (conduciendo, descanso, ralentí) → 24 h. Es el marco
          natural de una jornada y hace que los tres aros se puedan comparar
          entre sí de un vistazo.

        tráfico → el tiempo conduciendo, porque es un SUBCONJUNTO suyo. Sobre
          24 h daría un 3 % que no dice nada; sobre el tiempo al volante dice
          «de lo que manejó, esto fue a vuelta de rueda», que es la pregunta.

        conteos (casos, movimientos, tipos de cámara) → el total de hechos.

        absolutos (kilómetros, zonas recurrentes) → sin denominador: aro lleno.
          No afirma «100 %», afirma «esto no es una proporción».
    */
    const porDia = (min: number | null | undefined) =>
      min != null && Number.isFinite(min) ? Math.min(min / DIA_MIN, 1) : null;

    const conduciendo = jornada?.conduciendo_min ?? 0;
    const porConduccion = (min: number | null | undefined) =>
      conduciendo > 0 && min != null && Number.isFinite(min)
        ? Math.min(min / conduciendo, 1)
        : null;

    const totalHechos =
      (resumen?.movimientos ?? movimientos.length) +
      (resumen?.incidencias ?? incidencias.length) +
      conductas.reduce((n, c) => n + c.veces, 0);
    const porHecho = (n: number) => (totalHechos > 0 ? n / totalHechos : null);

    const movs = resumen?.movimientos ?? movimientos.length;
    const incs = resumen?.incidencias ?? incidencias.length;

    // El ralentí medido es `null` casi siempre: la ignición viaja en la trama
    // de estado, que no lleva coordenadas y por eso el histórico la descarta.
    // Cuando falta se enseña el estimado, y la tarjeta lo dice.
    const ralentiMedido = jornada?.ralenti_min ?? null;
    const ralentiEstimado = jornada?.ralenti_estimado_min ?? null;
    const ralenti = ralentiMedido ?? ralentiEstimado;
    const ralentiEsEstimado = ralentiMedido === null && ralentiEstimado != null;

    // Cuando el dato vino del OTRO equipo, la tarjeta lo dice. No es un
    // detalle técnico: esta pestaña es parte de un expediente que sirve para
    // deslindar responsabilidades, y ahí un número sin procedencia no vale.
    // Además explica por qué esta unidad tiene ralentí real y la de al lado no.
    // La misma nota para los dos: dicen de qué equipo salieron, por lo mismo
    // que el ralentí. Un dato de motor sin procedencia no sirve para deslindar.
    const notaDelMotor =
      motor && pareja
        ? t("monitoreoIa.conduccion.medidoPor", {
            defaultValue: "medido por {{equipo}}",
            equipo: pareja.nombre || t("monitoreoIa.conduccion.equipoCompanero", { defaultValue: "el segundo equipo" }),
          })
        : null;

    const ralentiNota = ralentiEsEstimado
      ? t("monitoreoIa.conduccion.ralentiEstimado", { defaultValue: "estimado" })
      : ralentiMedido !== null && pareja && pareja.emparejados > 0
        ? t("monitoreoIa.conduccion.medidoPor", {
            defaultValue: "medido por {{equipo}}",
            equipo: pareja.nombre || t("monitoreoIa.conduccion.equipoCompanero", { defaultValue: "el segundo equipo" }),
          })
        : null;

    return [
      { k: "tiempoConduciendo", etiqueta: null, valor: duracion(jornada?.conduciendo_min),
        fraccion: porDia(jornada?.conduciendo_min), tono: "success", nota: null },
      { k: "kilometros", etiqueta: null, valor: jornada ? `${jornada.km} km` : null,
        fraccion: null, tono: "info", nota: null },
      { k: "descanso", etiqueta: null, valor: duracion(jornada?.descanso_min),
        fraccion: porDia(jornada?.descanso_min), tono: "info", nota: null },
      { k: "trafico", etiqueta: null, valor: duracion(jornada?.trafico_min),
        fraccion: porConduccion(jornada?.trafico_min), tono: "warning", nota: null },
      { k: "ralenti", etiqueta: null, valor: duracion(ralenti),
        fraccion: porDia(ralenti), tono: "info", nota: ralentiNota },
      { k: "movimientos", etiqueta: null, valor: movs, fraccion: porHecho(movs), tono: "info", nota: null },
      // Se llama «Casos» y no «Incidencias» a propósito: es lo que Centinela
      // abrió y atendió, y así se llama en el resto del producto. «Incidencias»
      // junto a una pestaña «Evidencias» se confundían de oído y de vista.
      { k: "incidencias", etiqueta: null, valor: incs, fraccion: porHecho(incs), tono: "error", nota: null },
      { k: "zonasRecurrentes", etiqueta: null, valor: costumbres?.lugares_frecuentes ?? null,
        fraccion: null, tono: "success", nota: null },
      /*
        ── El motor ──────────────────────────────────────────────────────────
        Sólo aparecen cuando la unidad trae un equipo que lee la computadora
        del camión. Y de todo lo que manda se enseñan DOS, no un tablero:

        `temperatura máxima` y `voltaje mínimo` son los dos que explican una
        jornada rara sin que nadie tenga que ir a buscar. Un motor que llegó a
        104 °C dice por qué se detuvo a media tarde; un voltaje que se cayó a
        23 V dice por qué no arrancó al día siguiente. El resto del tablero
        —rpm, carga, códigos— vive en la ficha táctica, que es donde se pregunta
        «¿cómo está ahora?».

        Son EXTREMOS del día, no el valor actual: esta pestaña mira hacia atrás.
      */
      ...(motor
        ? [
            { k: "temperaturaMax", etiqueta: null,
              valor: typeof motor.extremos?.coolant_temperature === "number"
                ? `${motor.extremos.coolant_temperature} °C` : null,
              fraccion: null, tono: "warning" as const, nota: notaDelMotor },
            { k: "voltajeMin", etiqueta: null,
              valor: typeof motor.extremos?.external_power_voltage === "number"
                ? `${(motor.extremos.external_power_voltage / 1000).toFixed(1)} V` : null,
              fraccion: null, tono: "info" as const, nota: notaDelMotor },
          ]
        : []),
      /*
        Una tarjeta por CONDUCTA concreta —salida de carril, distracción,
        microsueño— y no por familia. El resumen nombra la conducta («once
        salidas de carril») y la rejilla mostraba la familia («Asistencia a la
        conducción»): quien leía el texto buscaba su número y no lo encontraba.

        Reemplazan a los contadores por familia en vez de sumarse a ellos: los
        dos juntos contaban los mismos hechos dos veces y el reparto del aro
        dejaba de cuadrar.
      */
      ...conductas.map((c) => ({
        k: c.signal,
        etiqueta: c.etiqueta,
        valor: c.veces,
        fraccion: porHecho(c.veces),
        tono: "warning",
        nota: null,
      })),
    ];
  }, [resumen, movimientos.length, incidencias.length, costumbres, conductas, jornada, t]);

  /**
   * Lo que hay detrás de un contador, cuando hay algo.
   *
   * Solo los que agrupan hechos: los tiempos y los kilómetros no tienen
   * "cuáles", son una medición. Devuelve `null` para esos, y la tarjeta
   * entonces no se vuelve clicable — un botón que no hace nada es peor que no
   * tener botón.
   */
  const detalleDe = useMemo(() => {
    const porKind = new Map<string, { hora: string; etiqueta: string }[]>();
    for (const e of cameraEvents) {
      const lista = porKind.get(e.kind) || [];
      lista.push({
        hora: new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        etiqueta: e.label,
      });
      porKind.set(e.kind, lista);
    }

    return (k: string) => {
      if (k === "incidencias") return agrupar(hechos.filter((h) => h.tipo === "caso"));
      if (k === "movimientos") return agrupar(hechos.filter((h) => h.tipo === "movimiento"));
      const conducta = conductas.find((c) => c.signal === k);
      if (conducta) {
        return [{
          etiqueta: conducta.etiqueta,
          veces: conducta.veces,
          primera: conducta.primera_hora,
          ultima: conducta.ultima_hora,
        }];
      }
      if (k === "zonasRecurrentes") {
        // Ya vienen agrupadas y contadas del expediente; solo se les da la
        // forma del resto. La «hora» aquí es la última visita, no un rango.
        return (costumbres?.lugares ?? []).map((l) => ({
          etiqueta:
            l.direccion ||
            `${l.lat.toFixed(4)}, ${l.lon.toFixed(4)}`,
          veces: l.veces,
          primera: l.ultima_vez
            ? new Date(l.ultima_vez).toLocaleDateString([], { day: "2-digit", month: "short" })
            : "",
          ultima: l.ultima_vez
            ? new Date(l.ultima_vez).toLocaleDateString([], { day: "2-digit", month: "short" })
            : "",
        }));
      }
      const deCamara = porKind.get(k);
      return deCamara ? agrupar(deCamara) : null;
    };
  }, [hechos, cameraEvents, costumbres, conductas]);

  /**
   * La tabla que se descarga.
   *
   * Arranca con los TRAMOS de la jornada —conduciendo, ralentí, detenida, sin
   * reportar— y no solo con los hechos. Un archivo que lista veinte alertas sin
   * decir qué estuvo haciendo el vehículo entre una y otra no es una bitácora,
   * es una lista de excepciones: se lee lo que salió mal y nunca lo que pasó.
   * Con los tramos, el día se recorre de principio a fin.
   */
  const filas = useMemo<FilaParte[]>(() => {
    const out: FilaParte[] = [];
    const hhmm = (ms: number) =>
      new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const coords = (lat: number | null, lon: number | null) =>
      lat != null && lon != null ? `${lat.toFixed(5)}, ${lon.toFixed(5)}` : "";

    (jornada?.tramos ?? []).forEach((tr, idx) => {
      const mins = Math.round((tr.hasta - tr.desde) / 60000);
      out.push({
        id: `t-${idx}`,
        hora: `${hhmm(tr.desde)} – ${hhmm(tr.hasta)}`,
        seccion: "Jornada",
        evento: ESTADO_TRAMO[tr.estado] ?? tr.estado,
        detalle: duracion(mins) ?? "",
        // La dirección gana sobre las coordenadas: «Av. 5 de Febrero 1200» se
        // entiende y «20.58810, -100.38890» hay que ir a buscarlo a un mapa.
        ubicacion: tr.direccion || coords(tr.lat, tr.lon),
      });
    });

    conductas.forEach((c, idx) => {
      out.push({
        id: `c-${c.signal}-${idx}`,
        hora: c.primera_hora === c.ultima_hora ? c.primera_hora : `${c.primera_hora} – ${c.ultima_hora}`,
        seccion: "Conducta",
        evento: c.etiqueta,
        detalle: `${c.veces} ${c.veces === 1 ? "vez" : "veces"}`,
        ubicacion: "",
      });
    });

    hechos.forEach((h, idx) => {
      if (h.tipo === "conducta") return; // ya van arriba, agrupadas
      out.push({
        id: `h-${idx}`,
        hora: h.hora,
        seccion: h.tipo === "caso" ? "Caso" : "Movimiento",
        evento: h.etiqueta,
        detalle: [h.estado, h.ubicado ? "" : "sin ubicar"].filter(Boolean).join(" · "),
        // La geocerca gana sobre las coordenadas: «Patio Querétaro» se entiende
        // y «20.58810, -100.38890» hay que ir a buscarlo a un mapa.
        ubicacion: h.geocerca || h.direccion || coords(h.lat, h.lon),
      });
    });

    conversaciones.forEach((l, idx) => {
      out.push({
        id: `l-${idx}`,
        hora: l.hora,
        seccion: "Llamada",
        evento: l.contesto ? "Contestada" : "Sin contestar",
        detalle: l.dijo || (l.duracion_seg ? `${l.duracion_seg} s` : ""),
        ubicacion: "",
      });
    });

    // Cronológico de punta a punta: un archivo agrupado por sección obliga a
    // saltar entre bloques para reconstruir la hora, que es como se lee un día.
    return out.sort((a, b) => a.hora.localeCompare(b.hora));
  }, [jornada, conductas, hechos, conversaciones]);

  const nombre = unidad?.nombre || unidad?.placa || unidad?.device_id || "unidad";
  const hayCamara = tieneCamara || cameraEvents.length > 0;

  const indice = useMemo(
    () => (parte ? indiceDelDia(conductas, { hayCamara }) : null),
    [parte, conductas, hayCamara],
  );
  const formulaIndice = t("monitoreoIa.conduccion.formula", {
    defaultValue:
      "100 menos el peso de cada conducta por la raíz de las veces que ocurrió. Provisional: todavía no se divide entre kilómetros ni horas al volante, así que sirve para comparar a esta unidad consigo misma, no contra otro conductor.",
  });

  // El mismo selector que usa Evidencias: dos pestañas del mismo modal no
  // pueden manejar las fechas de formas distintas.
  const selector = onFecha ? <SelectorDeDia dia={dia} onDia={onFecha} max={hoy} /> : null;

  return (
    <div className="min-w-0 space-y-4 max-lg:space-y-5">
      {/* La barra queda montada aunque el día venga vacío o cargando: si
          desapareciera, no habría forma de moverse a otro día. */}
      <div className="flex flex-wrap items-center justify-between gap-2 max-lg:gap-3">
        {selector}
        {/* La regla va junto a las descargas porque es lo mismo: llevarse el
            día. Excel y PDF para archivarlo, la regla para leerlo aquí. */}
        <button
          type="button"
          onClick={() => setVerRegla((v) => !v)}
          aria-expanded={verRegla}
          className={`ml-auto inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm max-lg:min-h-10 max-lg:px-4 font-medium transition-colors ${
            verRegla
              ? "border-info/40 bg-info/10 text-info"
              : "border-outline/40 text-content-secondary hover:bg-surface"
          }`}
        >
          <i className="ri-bar-chart-horizontal-line" aria-hidden="true" />
          <span className="hidden sm:inline">
            {t("monitoreoIa.conduccion.verRegla", { defaultValue: "Regla" })}
          </span>
        </button>
        <ExportButtons
          rows={filas}
          columns={COLUMNAS}
          meta={{
            title: `Conducción · ${nombre}`,
            period: dia,
            fileBase: `conduccion_${nombre}_${dia}`.replace(/\s+/g, "-"),
          }}
          analyticsSource="centinela_conduccion"
        />
      </div>

      {/*
        Se sigue esperando mientras haya una consulta en vuelo y todavía no
        haya parte. Con sólo `isLoading`, al cambiar de día se colaba un
        parpadeo de «No hay parte para este día» entre que la consulta anterior
        terminaba y la nueva traía datos — y eso no es un vacío, es una espera.
      */}
      {isLoading || (isFetching && !parte) ? (
        <CentinelaPensando />
      ) : !parte ? (
        <p className="rounded-xl bg-surface px-3 py-2 text-xs text-content-faint">
          {t("monitoreoIa.conduccion.sinParte", { defaultValue: "No hay parte para este día." })}
        </p>
      ) : (
        <div className={isFetching ? "space-y-5 opacity-60 max-lg:space-y-6" : "space-y-5 max-lg:space-y-6"}>
          <div className="flex items-start gap-3 max-sm:flex-col max-sm:items-center max-sm:gap-4">
            <Anillo valor={indice} formula={formulaIndice} />
            {narrativa ? (
              <p className="min-w-0 flex-1 rounded-xl bg-surface px-3 py-2 text-xs leading-relaxed text-content-secondary max-lg:p-3 max-lg:text-[13px] max-sm:w-full max-sm:flex-none">
                {narrativa}
              </p>
            ) : null}
          </div>

          {/*
            ── Los dos equipos se separaron ──────────────────────────────────
            Va ARRIBA de los indicadores, no como nota al pie, porque cambia
            cómo hay que leerlos todos: si los aparatos no iban en el mismo
            vehículo, los datos de motor de este día no son de este camión.

            El emparejamiento se configura una vez y nadie se acuerda de
            quitarlo cuando mueven un equipo. Por eso el servidor lo detecta
            solo y la pantalla lo grita: una excepción silenciosa que miente es
            peor que no tener la excepción.
          */}
          {pareja?.divergencia ? (
            <div className="rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-xs leading-relaxed text-content-secondary max-lg:p-3">
              <span className="font-semibold text-warning">
                {t("monitoreoIa.conduccion.parejaSeparada", {
                  defaultValue: "Los dos equipos de esta unidad no iban juntos",
                })}
              </span>{" "}
              {t("monitoreoIa.conduccion.parejaSeparadaDetalle", {
                defaultValue:
                  "{{equipo}} estuvo hasta {{km}} km de distancia durante {{minutos}} minutos, así que se dejaron de mezclar sus datos. Revisa si el equipo se cambió de vehículo.",
                equipo:
                  pareja.nombre ||
                  t("monitoreoIa.conduccion.equipoCompanero", { defaultValue: "el segundo equipo" }),
                km: Math.round(pareja.divergencia.distancia_max_m / 100) / 10,
                minutos: pareja.divergencia.minutos,
              })}
            </div>
          ) : null}

          {/* ── Cómo condujo ─────────────────────────────────────────────── */}
          <div className="max-lg:border-t max-lg:border-line max-lg:pt-5">
            {/* Cuatro columnas y tarjeta vertical. En dos columnas con el aro
                al lado, diez indicadores se apilaban en cinco filas anchas y
                se leían como una lista, no como un tablero. Más chicas y en
                rejilla se abarcan de un vistazo, que es para lo que están. */}
            <div className="grid grid-cols-2 gap-2.5 min-[480px]:grid-cols-3 sm:grid-cols-4 sm:gap-1.5">
              {indicadores.map(({ k, etiqueta, valor, fraccion, tono, nota }) => {
                const pendiente = valor === null;
                const detalle = pendiente ? null : detalleDe(k);
                const abrible = Boolean(detalle && detalle.length > 0);
                const activo = abierto === k;
                const nombre = etiqueta ?? t(`monitoreoIa.conduccion.${k}`, { defaultValue: k });

                const contenido = (
                  <>
                    <Reloj fraccion={fraccion} tono={tono} pendiente={pendiente} />
                    <span
                      className={
                        pendiente
                          ? "mt-1 block w-full truncate text-[13px] font-semibold tabular-nums text-content-faint"
                          : "mt-1 block w-full truncate text-[13px] font-semibold tabular-nums text-content"
                      }
                    >
                      {pendiente ? "—" : valor}
                    </span>
                    <span className="block w-full truncate font-mono text-[8px] uppercase leading-tight tracking-wider text-content-muted max-lg:mt-0.5 max-lg:whitespace-normal max-lg:break-words max-lg:text-[11px]">
                      {nombre}
                    </span>
                    {/* Una estimación con la misma cara que una medición es
                        peor que un guion: se usa creyendo que es un dato. */}
                    {nota && (
                      <span className="mt-0.5 block w-full truncate rounded-full bg-subtle text-[7.5px] uppercase tracking-wider text-content-faint max-lg:mt-1 max-lg:text-[11px]">
                        {nota}
                      </span>
                    )}
                  </>
                );

                const base =
                  "flex min-w-0 flex-col items-center rounded-xl px-1.5 py-2 text-center transition-colors max-lg:px-2 max-lg:py-3";

                return abrible ? (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setAbierto(activo ? null : k)}
                    aria-expanded={activo}
                    title={t("monitoreoIa.conduccion.verCuales", {
                      defaultValue: "Ver cuáles",
                    })}
                    className={`${base} ${
                      activo ? "bg-info/10 ring-1 ring-info/40" : "bg-surface hover:bg-subtle"
                    }`}
                  >
                    {contenido}
                  </button>
                ) : (
                  <div
                    key={k}
                    className={`${base} ${
                      pendiente ? "border border-dashed border-outline/40" : "bg-surface"
                    }`}
                  >
                    {contenido}
                  </div>
                );
              })}
            </div>

            {/* Lo que hay detrás del contador que se abrió. Va aquí abajo y no
                dentro de la tarjeta para no descuadrar la rejilla. */}
            {abierto && (detalleDe(abierto)?.length ?? 0) > 0 && (
              <ul className="mt-1.5 space-y-1 max-lg:mt-3 max-lg:space-y-1.5">
                {detalleDe(abierto)!.map((g) => (
                  <li
                    key={g.etiqueta}
                    className="flex items-center justify-between gap-3 rounded-lg bg-surface px-2.5 py-1.5 max-lg:px-3 max-lg:py-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="min-w-[2ch] text-xs font-semibold tabular-nums text-content">
                        {g.veces}
                      </span>
                      <span className="min-w-0 truncate text-[11px] text-content-secondary">
                        {g.etiqueta}
                      </span>
                    </span>
                    <span className="shrink-0 font-mono text-[9px] text-content-faint max-lg:text-[11px]">
                      {g.primera === g.ultima ? g.primera : `${g.primera} – ${g.ultima}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {verRegla && (
            <ReglaDelDia medicion={jornada} inicioDia={inicioDia} />
          )}

          {/* La evidencia se mudó a su propia pestaña: aquí competía por
              espacio con los números y para llegar a una foto había que pasar
              por ocho indicadores y la regla. */}
          {!hayCamara && (
            <p className="rounded-xl bg-surface px-3 py-2 text-xs text-content-faint max-lg:p-3">
              {t("monitoreoIa.conduccion.sinCamara", {
                defaultValue:
                  "Esta unidad no trae cámara, así que no hay conducta que observar. El resto del parte sí aplica.",
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
